import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getUsers, getUserDetails, getCategoryStats } from '../api';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SearchPanel from '../components/SearchPanel';
import Icon from '../components/Icon';
import UsersSortFiltersMenu from '../components/UsersSortFiltersMenu';
import UsersActionsMenu from '../components/UsersActionsMenu';
import PageSizeDropdown from '../components/PageSizeDropdown';
import CustomSelect from '../components/CustomSelect';
import CopyButton from '../components/CopyButton';
import verifiedProviderBadge from '../assets/verified-provider-badge.png';
import '../css/users.css';
import '../css/cards.css';
import '../css/pagination.css';
import '../css/list-summary.css';
import { pushDetailNavState } from '../utils/detailNavState';

const STORAGE_KEY = 'usersListState';
const defaultFilters = {
  name: '',
  phone: '',
  role: '',
  status: '',
  verified: '',
  marketingConsent: '',
  createdFrom: '',
  createdTo: '',
  id: '',
  ratingFrom: '',
  ratingTo: '',
  categoryId: '',
  regionCodes: [],
  ordersCount: '',
  ordersCountOrMore: false,
};
const defaultSortParams = { sortBy: 'createDate', direction: 'DESC' };
const ROLE_ICON_MAP = {
  DEV: 'zap',
  ADMIN: 'shield',
  MODERATOR: 'activity',
  SUPPORT: 'message-square',
  PROVIDER: 'briefcase',
  CUSTOMER: 'user',
};

const normalizeSalutUserFilters = (raw) => {
  const base = { ...defaultFilters };
  if (!raw || typeof raw !== 'object') return base;
  const { categoryIds, ...rest } = raw;
  let categoryId = '';
  if (rest.categoryId != null && String(rest.categoryId).trim() !== '') {
    categoryId = String(rest.categoryId).trim();
  } else if (Array.isArray(categoryIds) && categoryIds.length > 0) {
    categoryId = String(categoryIds[0]).trim();
  }
  const regionCodes = Array.isArray(rest.regionCodes)
    ? rest.regionCodes.filter((c) => c != null && c !== '').map((c) => Number(c))
    : [];

  return {
    ...base,
    ...rest,
    categoryId,
    regionCodes,
  };
};

const escapeCsvCell = (val) => {
  const s = val == null ? '' : String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const ADMIN_EXPORT_ROLES = ['ADMIN', 'DEV'];
const EXPORT_PAGE_SIZE = 100000;

const fetchUsersPage = (filters, sortParams, page, size) =>
  getUsers(
    page,
    size,
    sortParams.sortBy,
    filters.name,
    filters.phone,
    filters.role,
    filters.status,
    sortParams.direction,
    filters.createdFrom,
    filters.createdTo,
    filters.id,
    filters.ratingFrom,
    filters.ratingTo,
    filters.categoryId,
    filters.verified,
    filters.regionCodes,
    filters.ordersCount,
    filters.ordersCountOrMore,
    filters.marketingConsent
  );

const downloadCsv = (headers, rows, filenamePrefix) => {
  const csv = [headers.join(','), ...rows.map((r) => r.map(escapeCsvCell).join(','))].join('\r\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const downloadUsersCsv = (list, filenamePrefix) => {
  const headers = ['id', 'fullName', 'defaultPhoneNumber'];
  const rows = list.map((u) => [u.id, u.fullName ?? '', u.defaultPhoneNumber ?? '']);
  downloadCsv(headers, rows, filenamePrefix);
};

// Полный набор полей (включая createdOrdersCount/purchasedOrdersCount/takenOrdersCount/
// totalPaid/paymentsCount/payments), доступен только ADMIN/DEV и не отображается в карточках списка.
const downloadAdminReportCsv = (list, filenamePrefix) => {
  const headerSet = new Set();
  list.forEach((u) => Object.keys(u).forEach((key) => headerSet.add(key)));
  const headers = Array.from(headerSet);
  const rows = list.map((u) =>
    headers.map((h) => {
      const val = u[h];
      if (val == null) return '';
      return typeof val === 'object' ? JSON.stringify(val) : val;
    })
  );
  downloadCsv(headers, rows, filenamePrefix);
};

const getCurrentUserRole = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw)?.role ?? null;
  } catch {
    return null;
  }
};

const USER_CARD_HEADER_STYLE = {
  alignItems: 'center',
  minHeight: 'unset',
  marginBottom: '0',
  paddingBottom: '8px',
};

const USER_CARD_HEADER_MAIN_STYLE = { flex: 1, minWidth: 0, marginLeft: '4px' };
const USER_CARD_ROLE_ROW_STYLE = { display: 'flex', alignItems: 'center', width: '100%', gap: '8px' };
const USER_CARD_FOOTER_STYLE = { paddingTop: '8px' };

const Users = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'ro' ? 'ro-RO' : 'en-US';
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState({
    totalPages: 0,
    totalElements: 0,
    size: 10,
    number: 0,
    first: true,
    last: true
  });
  const [sortParams, setSortParams] = useState(defaultSortParams);
  const [initialized, setInitialized] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(false);
  const isAdminExport = useMemo(() => ADMIN_EXPORT_ROLES.includes(getCurrentUserRole()), []);

  const loadCategories = useCallback(async () => {
    if (categories.length > 0 || categoriesLoading) return;
    setCategoriesLoading(true);
    setCategoriesError(false);
    try {
      const data = await getCategoryStats();
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setCategories([]);
      setCategoriesError(true);
    } finally {
      setCategoriesLoading(false);
    }
  }, [categories.length, categoriesLoading]);

  // Восстановление состояния при монтировании: из location.state (возврат из деталей) или из sessionStorage
  useEffect(() => {
    const fromState = location.state?.fromUsers;
    const storedRaw = sessionStorage.getItem(STORAGE_KEY);

    if (fromState) {
      setCurrentPage(fromState.currentPage ?? 0);
      setFilters(normalizeSalutUserFilters(fromState.filters));
      setSortParams(fromState.sortParams ?? defaultSortParams);
      setPaginationInfo(prev => ({ ...prev, size: fromState.size ?? prev.size }));
    } else if (storedRaw) {
      try {
        const parsed = JSON.parse(storedRaw);
        setCurrentPage(parsed.currentPage ?? 0);
        setFilters(normalizeSalutUserFilters(parsed.filters));
        setSortParams(parsed.sortParams ?? defaultSortParams);
        setPaginationInfo(prev => ({ ...prev, size: parsed.size ?? prev.size }));
      } catch (e) {
        console.warn('Failed to parse usersListState', e);
      }
    }
    setInitialized(true);
  }, [location.state]);

  // Сохранение состояния списка в sessionStorage
  useEffect(() => {
    if (!initialized) return;
    const listState = {
      currentPage,
      filters,
      sortParams,
      size: paginationInfo.size
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(listState));
  }, [initialized, currentPage, filters, sortParams, paginationInfo.size]);

  const isFirstLoad = useRef(true);
  const topPagerRef = useRef(null);
  const shouldScrollToPagerRef = useRef(false);

  const rolesWithIcons = useMemo(
    () => [
      { value: '', label: t('users.filters.allRoles'), icon: 'layers' },
      { value: 'CUSTOMER', label: t('users.roles.CUSTOMER'), icon: ROLE_ICON_MAP.CUSTOMER },
      { value: 'PROVIDER', label: t('users.roles.PROVIDER'), icon: ROLE_ICON_MAP.PROVIDER },
      { value: 'SUPPORT', label: t('users.roles.SUPPORT'), icon: ROLE_ICON_MAP.SUPPORT },
      { value: 'MODERATOR', label: t('users.roles.MODERATOR'), icon: ROLE_ICON_MAP.MODERATOR },
      { value: 'ADMIN', label: t('users.roles.ADMIN'), icon: ROLE_ICON_MAP.ADMIN },
      { value: 'DEV', label: t('users.roles.DEV'), icon: ROLE_ICON_MAP.DEV },
    ],
    [t]
  );

  const statuses = [
    { value: '', label: t('users.filters.allStatuses'), icon: 'layers' },
    { value: 'ACTIVE', label: t('users.statuses.ACTIVE'), icon: 'check-circle' },
    { value: 'INACTIVE', label: t('users.statuses.INACTIVE'), icon: 'clock' },
    { value: 'BLOCKED', label: t('users.statuses.BLOCKED'), icon: 'slash' }
  ];

  const marketingConsentOptions = [
    { value: '', label: t('users.filters.allMarketingConsent'), icon: 'layers' },
    { value: 'true', label: t('users.filters.marketingConsentYes'), icon: 'mail' },
    { value: 'false', label: t('users.filters.marketingConsentNo'), icon: 'slash' },
  ];

  const verificationOptions = [
    { value: '', label: t('users.filters.allVerification'), icon: 'layers' },
    { value: 'true', label: t('users.filters.verifiedOnly'), icon: 'check-circle' },
    { value: 'false', label: t('users.filters.notVerifiedOnly'), icon: 'slash' },
  ];


  useEffect(() => {
    if (!initialized) return;
    const loadUsers = async () => {
      setLoading(true);
      try {
        // SMART SEARCH: If a full UUID is provided in the ID field, try direct fetch first
        if (filters.id && filters.id.trim().length >= 32) {
          try {
            const directUser = await getUserDetails(filters.id.trim());
            if (directUser) {
              setUsers([directUser]);
              setPaginationInfo({
                totalPages: 1,
                totalElements: 1,
                size: paginationInfo.size,
                number: 0,
                first: true,
                last: true
              });
              setLoading(false);
              return;
            }
          } catch (e) {
            console.warn('Direct fetch by ID failed, falling back to global search:', e);
          }
        }

        const data = await fetchUsersPage(filters, sortParams, currentPage, paginationInfo.size);
        setUsers(data.content || []);
        setPaginationInfo({
          totalPages: data.totalPages || 0,
          totalElements: data.totalElements || 0,
          size: data.size || paginationInfo.size,
          number: data.number || 0,
          first: data.first || false,
          last: data.last || false
        });
      } catch (err) {
        console.error('Ошибка загрузки пользователей:', err);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [initialized, filters, currentPage, paginationInfo.size, sortParams]);

  useEffect(() => {
    if (!initialized) return;
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }
    setCurrentPage(0);
  }, [initialized, filters, sortParams]);

  const [searchValue, setSearchValue] = useState(filters.name);

  // Sync internal search value with filters (e.g. on reset)
  useEffect(() => {
    setSearchValue(filters.name);
  }, [filters.name]);

  // Debounce search input (сбрасываем страницу только если имя в фильтре реально меняется)
  useEffect(() => {
    if (!initialized) return;
    const timer = setTimeout(() => {
      setFilters((prev) => {
        if (prev.name === searchValue) return prev;
        setCurrentPage(0);
        return { ...prev, name: searchValue };
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue, initialized]);


  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setSearchValue('');
    setFilters({ ...defaultFilters });
    setSortParams(defaultSortParams);
    setCurrentPage(0);
  };

  const handleSelectCategory = (idStr) => {
    setFilters((prev) => ({ ...prev, categoryId: String(idStr).trim() }));
  };

  const clearCategoryFilter = () => {
    setFilters((prev) => ({ ...prev, categoryId: '' }));
  };

  // Restore helper functions
  const handlePageChange = (newPage, shouldScroll = false) => {
    if (newPage >= 0 && newPage < paginationInfo.totalPages) {
      if (shouldScroll) {
        shouldScrollToPagerRef.current = true;
      }
      setCurrentPage(newPage);
    }
  };

  useEffect(() => {
    if (!shouldScrollToPagerRef.current) return;
    if (loading) return;

    const el = topPagerRef.current;
    if (!el) {
      const t = setTimeout(() => {
        const elRetry = topPagerRef.current;
        if (!shouldScrollToPagerRef.current) return;
        if (!elRetry) return;
        shouldScrollToPagerRef.current = false;
        elRetry.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
      return () => clearTimeout(t);
    }

    shouldScrollToPagerRef.current = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentPage, loading]);

  const getVisiblePages = () => {
    const totalPages = paginationInfo.totalPages || 0;
    const current = currentPage;
    if (totalPages <= 1) return [];
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);

    const pages = [0];
    const left = Math.max(1, current - 1);
    const right = Math.min(totalPages - 2, current + 1);
    if (left > 1) pages.push('left-ellipsis');
    for (let p = left; p <= right; p += 1) pages.push(p);
    if (right < totalPages - 2) pages.push('right-ellipsis');
    pages.push(totalPages - 1);
    return pages;
  };

  const renderUsersPager = (position = 'bottom') => {
    if ((loading && users.length === 0) || paginationInfo.totalPages <= 0) return null;

    const isFirst = currentPage <= 0;
    const isLast = currentPage >= paginationInfo.totalPages - 1;
    const pages = getVisiblePages();

    const total = paginationInfo.totalElements || 0;
    const from = total > 0 ? currentPage * paginationInfo.size + 1 : 0;
    const to = Math.min((currentPage + 1) * paginationInfo.size, total);

    return (
      <div
        className={`orders-pager orders-pager-${position}`}
        ref={position === 'top' ? topPagerRef : null}
      >
        <div className="orders-pager-info">
          {t('common.pagination.showing', { from, to, total })}
        </div>

        <div className="orders-pager-controls">
          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => handlePageChange(currentPage - 1, position === 'bottom')}
            disabled={isFirst}
          >
            {t('common.pagination.previous')}
          </button>

          <div className="orders-pager-pages">
            {pages.map((item) => {
              if (typeof item !== 'number') {
                return <span key={item} className="orders-pager-ellipsis">...</span>;
              }
              const active = item === currentPage;
              return (
                <button
                  key={item}
                  type="button"
                  className={`orders-pager-page ${active ? 'active' : ''}`}
                  onClick={() => handlePageChange(item, position === 'bottom')}
                  aria-current={active ? 'page' : undefined}
                >
                  {item + 1}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => handlePageChange(currentPage + 1, position === 'bottom')}
            disabled={isLast}
          >
            {t('common.pagination.next')}
          </button>

          <PageSizeDropdown
            value={paginationInfo.size}
            onChange={(newSize) => {
              setPaginationInfo((prev) => ({ ...prev, size: parseInt(newSize, 10) }));
              shouldScrollToPagerRef.current = true;
              setCurrentPage(0);
            }}
            options={[10, 20, 50, 100, 200]}
            position={position}
          />
        </div>
      </div>
    );
  };

  const getRoleIconName = useCallback((role) => ROLE_ICON_MAP[role] || 'tag', []);

  const renderMiniRating = useCallback((rating) => {
    const val = parseFloat(rating) || 0;
    const starCount = Math.round(val);
    return (
      <div className="rating-mini">
        <span className="mini-stars">
          {[...Array(5)].map((_, i) => (
            <Icon
              key={i}
              name={i < starCount ? 'star-fill' : 'star'}
              size={14}
              className={i < starCount ? 'star-full' : 'star-empty'}
            />
          ))}
        </span>
        <span className="rating-value-mini">{val.toFixed(1)}</span>
      </div>
    );
  }, []);


  const translateRole = useCallback((role) => {
    const roleMap = t('users.roles', { returnObjects: true });
    return roleMap[role] || role;
  }, [t]);

  const handleSortChange = (newSort) => {
    setSortParams(prev => ({ ...prev, sortBy: newSort }));
    setCurrentPage(0);
  };

  const handleDirectionChange = (newDirection) => {
    setSortParams(prev => ({ ...prev, direction: newDirection }));
    setCurrentPage(0);
  };

  const filtersActive = useMemo(() => {
    const f = filters;
    const sp = sortParams;
    return Boolean(
      (f.name && f.name.trim()) ||
        (f.phone && f.phone.trim()) ||
        f.role ||
        f.status ||
        f.verified ||
        f.marketingConsent ||
        f.createdFrom ||
        f.createdTo ||
        f.ratingFrom ||
        f.ratingTo ||
        (f.id && String(f.id).trim()) ||
        (f.categoryId && String(f.categoryId).trim()) ||
        (Array.isArray(f.regionCodes) && f.regionCodes.length > 0) ||
        (f.ordersCount !== '' && f.ordersCount != null) ||
        sp.sortBy !== defaultSortParams.sortBy ||
        sp.direction !== defaultSortParams.direction
    );
  }, [filters, sortParams]);

  const usersListStateForLinks = useMemo(
    () => ({
      currentPage,
      filters,
      sortParams,
      size: paginationInfo.size,
    }),
    [currentPage, filters, sortParams, paginationInfo.size]
  );

  const roleFilter = (
    <CustomSelect
      value={filters.role || ''}
      options={rolesWithIcons}
      onChange={(val) => handleFilterChange('role', val)}
      minWidth="180px"
      hasActiveFilter={Boolean(filters.role)}
    />
  );

  const phoneFilter = (
    <div className="search-input-wrapper" style={{ width: '100%' }}>
      <input
        type="text"
        className="filter-input with-icon"
        value={filters.phone || ''}
        onChange={(e) => handleFilterChange('phone', e.target.value)}
        placeholder={t('users.filters.phonePlaceholder')}
      />
      <div className="search-input-icon">
        <Icon name="phone" size={18} />
      </div>
    </div>
  );

  // Экспорт всех пользователей по текущим фильтрам; audience === 'marketing' дополнительно
  // ограничивает выборку согласившимися на рассылку (marketingConsent=true).
  const handleExport = useCallback(async (kind, audience) => {
    const isMarketing = audience === 'marketing';
    const exportFilters = isMarketing ? { ...filters, marketingConsent: 'true' } : filters;
    try {
      const data = await fetchUsersPage(exportFilters, sortParams, 0, EXPORT_PAGE_SIZE);
      const all = data.content || [];
      if (!all.length) {
        window.alert(t('users.actions.empty'));
        return false;
      }
      const suffix = isMarketing ? 'mailing' : 'all';
      if (kind === 'report') downloadAdminReportCsv(all, `users-report-${suffix}`);
      else downloadUsersCsv(all, `users-${suffix}`);
      return true;
    } catch (err) {
      window.alert(err.message || t('users.actions.failed'));
      return false;
    }
  }, [filters, sortParams, t]);

  const requestMarketingCount = useCallback(async () => {
    const data = await fetchUsersPage({ ...filters, marketingConsent: 'true' }, sortParams, 0, 1);
    return data.totalElements ?? 0;
  }, [filters, sortParams]);

  const extraActions = (
    <UsersSortFiltersMenu
      sort={sortParams.sortBy}
      direction={sortParams.direction}
      onSortChange={handleSortChange}
      onDirectionChange={handleDirectionChange}
      status={filters.status}
      statusOptions={statuses}
      onStatusChange={(value) => handleFilterChange('status', value)}
      verified={filters.verified}
      verificationOptions={verificationOptions}
      onVerifiedChange={(value) => handleFilterChange('verified', value)}
      marketingConsent={filters.marketingConsent}
      marketingConsentOptions={marketingConsentOptions}
      onMarketingConsentChange={(value) => handleFilterChange('marketingConsent', value)}
      createdFrom={filters.createdFrom}
      createdTo={filters.createdTo}
      onDateFromChange={(value) => handleFilterChange('createdFrom', value)}
      onDateToChange={(value) => handleFilterChange('createdTo', value)}
      ratingFrom={filters.ratingFrom}
      ratingTo={filters.ratingTo}
      onRatingFromChange={(value) => handleFilterChange('ratingFrom', value)}
      onRatingToChange={(value) => handleFilterChange('ratingTo', value)}
      id={filters.id}
      onIdChange={(value) => handleFilterChange('id', value)}
      translationKey="users"
      categories={categories}
      selectedCategoryId={filters.categoryId}
      onSelectCategory={handleSelectCategory}
      onClearCategory={clearCategoryFilter}
      categoriesLoading={categoriesLoading}
      categoriesError={categoriesError}
      onRequestCategories={loadCategories}
      selectedRegionCodes={filters.regionCodes}
      onRegionCodesChange={(codes) => handleFilterChange('regionCodes', codes)}
      ordersCount={filters.ordersCount}
      ordersCountOrMore={filters.ordersCountOrMore}
      onOrdersCountChange={(value) => handleFilterChange('ordersCount', value)}
      onOrdersCountOrMoreChange={(value) => handleFilterChange('ordersCountOrMore', value)}
    />
  );

  return (
    <div className="users-page">
      <div className="users-content-surface" style={{ marginBottom: '24px' }}>
        <div className="list-summary-row">
          <div className="list-summary-header">
            <div className="list-summary-header-left">
              <div className="list-summary-icon">
                <Icon name="users" size={24} />
              </div>
              <div>
                <h3 className="list-summary-title">{t('users.title')}</h3>
                <p className="list-summary-subtitle">
                  {t('home.logs.subtitle', { count: paginationInfo.totalElements })}
                </p>
              </div>
            </div>
          </div>
          <UsersActionsMenu
            canGenerateReport={isAdminExport}
            totalCount={paginationInfo.totalElements}
            marketingOnlyFilter={filters.marketingConsent === 'true'}
            onRequestMarketingCount={requestMarketingCount}
            onExport={handleExport}
          />
        </div>

        <div className="search-panel-wrapper">
          <SearchPanel
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onReset={clearFilters}
            loading={loading}
            placeholder={t('users.filters.namePlaceholder')}
            statusFilter={roleFilter}
            secondaryFilter={phoneFilter}
            extraActions={extraActions}
            filtersActive={filtersActive}
          />
        </div>
      </div>

      <div className="users-content-surface">
        {renderUsersPager('top')}

        {loading && users.length === 0 ? (
          <div className="loading-state">
            <div className="strict-loader"></div>
            {t('users.loading')}
          </div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><Icon name="users" size={48} /></div>
            <h3>{t('users.empty.title')}</h3>
            <p>{t('users.empty.subtitle')}</p>
          </div>
        ) : (
          <div className="cards-grid" style={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
            {users.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                usersListState={usersListStateForLinks}
                getRoleIconName={getRoleIconName}
                renderMiniRating={renderMiniRating}
                translateRole={translateRole}
                t={t}
                locale={locale}
              />
            ))}
          </div>
        )}

        {renderUsersPager('bottom')}
      </div>
    </div>
  );
};

// Изменённый компонент: обёртка div превращена в Link
// Изменённый компонент: обёртка div превращена в Link
const UserCard = React.memo(({
  user,
  usersListState,
  getRoleIconName,
  renderMiniRating,
  translateRole,
  t,
  locale
}) => {
  const location = useLocation();
  const formatDate = (dateString, fallbackDate) => {
    const dateToFormat = dateString || fallbackDate;
    if (!dateToFormat) return '—';
    try {
      const date = new Date(dateToFormat);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${day}.${month}.${year}, ${hours}:${minutes}`;
    } catch {
      return '—';
    }
  };

  const renderUserAvatar = () => {
    const name = user.fullName || user.username || '?';
    const firstLetter = name.trim().charAt(0).toUpperCase();
    const avatarStatusClass = `status-${String(user.status || '').toLowerCase()}`;

    return (
      <div className={`user-avatar-container ${avatarStatusClass}`}>
        {(user.avatar || user.avatarUrl) ? (
          <img
            src={user.avatar || user.avatarUrl}
            alt={name}
            className="user-avatar-img"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span className="user-avatar-initial">{firstLetter}</span>
        )}
        <span className={`status-dot ${avatarStatusClass}`} aria-hidden="true"></span>
      </div>
    );
  };

  return (
    <Link
      to={`/users/${user.id}`}
      state={pushDetailNavState(location, { fromUsers: usersListState })}
      className="item-card clickable-card"
    >
      <div className="card-header" style={USER_CARD_HEADER_STYLE}>
        {renderUserAvatar()}
        <div style={USER_CARD_HEADER_MAIN_STYLE}>
          <div className={`user-card-title-row ${user.role === 'PROVIDER' && Boolean(user.verified) ? 'has-verified' : ''}`}>
            <h4 className="card-title user-card-title">
              {user.fullName || user.username || translateRole(user.role)}
            </h4>
            {user.role === 'PROVIDER' && Boolean(user.verified) && (
              <img
                src={verifiedProviderBadge}
                alt={t('users.labels.verified')}
                className="user-verified-image"
              />
            )}
          </div>
          {user.role === 'PROVIDER' && renderMiniRating(user.rating || user.companyRating || user.averageRating || 0)}
        </div>
      </div>

      <div className="card-body">
        <div className="card-body-rows">
          <div className="card-main-details" style={{ minHeight: 'unset', gap: '8px' }}>
            {/* 1 строчка: Роль */}
            <div className="card-row">
              <div className="card-row-item" style={USER_CARD_ROLE_ROW_STYLE}>
                <Icon name={getRoleIconName(user.role)} size={16} className="card-detail-icon" />
                <span className="card-detail-text role-text">{translateRole(user.role)}</span>
              </div>
            </div>

            {/* 2 строчка: Телефон */}
            <div className="card-row">
              <div className="card-row-item" title={t('users.filters.phone')}>
                <Icon name="phone" size={16} className="card-detail-icon" />
                <span className={`card-detail-text user-card-phone-text ${user.defaultPhoneNumber ? 'copiable-text' : ''}`}>
                  {user.defaultPhoneNumber || <span className="card-empty">—</span>}
                </span>
                {user.defaultPhoneNumber && <CopyButton text={user.defaultPhoneNumber} />}
              </div>
            </div>

            {/* 3 строчка: Email | Отзывы */}
            <div className="card-row">
              <div className="card-row-item" title="Email">
                <Icon name="mail" size={16} className="card-detail-icon" />
                <span className={`card-detail-text user-card-email-text ${user.email ? 'copiable-text' : ''} ${user.email ? 'is-email' : ''}`}>
                  {user.email || <span className="card-empty">—</span>}
                </span>
              </div>

              {user.role === 'CUSTOMER' ? (
                <div className="card-row-item" title={t('users.labels.orders')}>
                  <Icon name="orders" size={16} className="card-detail-icon" />
                  <span className="card-detail-text">
                    {user.createdOrdersCount || 0} {t('users.labels.orders')}
                  </span>
                </div>
              ) : (
                <div className="card-row-item" title={t('users.labels.reviews')}>
                  <Icon name="message-square" size={16} className="card-detail-icon" />
                  <span className="card-detail-text">
                    {user.countRating || 0} {t('users.labels.reviews')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card-footer" style={USER_CARD_FOOTER_STYLE}>
        <div className="card-footer-right">
          <div className="card-date">
            <Icon name="calendar" size={14} className="card-date-icon" />
            <span>{formatDate(user.createdAt, user.createDate)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
});

export default Users;